import { observer } from 'mobx-react';
import * as React from 'react';
import { Component } from 'react';
import {observable} from "mobx";
import {Redirect, Route} from "react-router";
import {RouteComponentProps} from "react-router-dom";
import { Button } from 'reactstrap';
import {UserApi} from "@intelingu/core/api/UserApi";
import {lazyInject} from "@intelingu/core/IoC/container";
import {AuthStore} from "../../../../stores/AuthStore";
import {AnalyticsStore} from "../../../../stores/AnalyticsStore";

interface StepRegisterPageProps extends RouteComponentProps<any> {
}

enum Errors { Password, Name, Email, EmailExists }

const validateEmail = (email) => {
    const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
};

@observer export class StepRegisterPage extends Component<StepRegisterPageProps, any> {

    @lazyInject(UserApi) userApi: UserApi;
    @lazyInject(AuthStore) authStore: AuthStore;
    @lazyInject(AnalyticsStore) analyticsStore: AnalyticsStore;

    @observable loading = false;
    @observable step = 1;
    @observable errors = [];
    references = {};

    data = {
        name: '',
        password: '',
        email: ''
    };

    get stepFromUrl() {
        const parts = document.location.pathname.split('/');
        return Number(parts[parts.length - 1]);
    }

    componentDidMount(): void {
        const urlStep = this.stepFromUrl;

        const defaultData = localStorage.getItem('stRegData');

        if (!defaultData) {
            this.step = 1;
            this.props.history.push('/st/register/1');
        } else {
            this.step = urlStep;
            this.data = JSON.parse(defaultData);
        }

        window.onpopstate = (e) => {
            this.step = this.stepFromUrl;
        }
    }

    error(name: any) {
        this.errors.push(name);
        this.loading = false;
        return;
    }

    async validate() {
        this.loading = true;
        this.errors = [];

        if (this.step === 4) {
            this.props.history.push('/');
            return;
        }

        if (this.step > 0 && !this.data.name.length) {
            return this.error(Errors.Name);
        }

        if (this.step > 1 && this.data.password.length < 6) {
            return this.error(Errors.Password);
        }

        if (this.step > 2 && !validateEmail(this.data.email)) {
            return this.error(Errors.Email);
        }

        if (this.step === 3) {
            const result = await this.userApi.checkEmail(this.data.email);

            if (!result.data.result) {
                return this.error(Errors.EmailExists);
            } else {
                const response = await this.userApi.register({ ...this.data, web_account: true });
                const { token } = response.data;

                this.analyticsStore.trackRegistrationCompleted();
                await this.authStore.authorizeWithToken(token);
            }
        }

        if (this.step === 3) {
            this.step = 4;
        } else {
            this.step += 1;
            setTimeout(() => this.references[this.step].focus());
        }

        this.loading = false;
        localStorage.setItem('stRegData', JSON.stringify(this.data));
        this.props.history.push('/st/register/' + this.step);
    }

    wrap(child: JSX.Element) {
        return <div className={`st-input ${this.errors.length > 0 ? 'error' : ''}`}>
            { child }
            { this.errors.includes(Errors.Password) && <div className={'error'}>Dein Passwort ist zu kurz. Es sollte aus mindestens 6 Zeichen bestehen.</div> }
            { this.errors.includes(Errors.EmailExists) && <div className={'error'}>E-Mail Adresse wird bereits verwendet</div> }
        </div>
    }

    step1() {
        return <>
            <h1>Wie heißt du?</h1>

            { this.wrap(<>
                <input
                    type={'text'}
                    defaultValue={this.data.name}
                    onChange={e => this.data.name = e.currentTarget.value}
                    ref={e => this.references[1] = e}
                    placeholder={'Name'} />
                <span />
            </>) }
        </>
    }

    step2() {
        return <>
            <h1>Bitte wähle ein Passwort</h1>

            { this.wrap(<>
                <input
                    defaultValue={this.data.password}
                    onChange={e => this.data.password = e.currentTarget.value}
                    type={'password'}
                    ref={e => this.references[2] = e}
                    placeholder={''} />
                <span className={'lock'} />
            </>) }

            <div className={'terms'}>Mit der kostenlosen Registrierung akzeptiere ich die <a href={'https://home.intelingu.com/datenschutz/'} target={'_blank'}>Datenschutzerklärung</a></div>
        </>
    }

    step3() {
        return <>
            <h1>Wie lautet deine E-Mail-Adresse?</h1>

            { this.wrap(<>
                <input
                    defaultValue={this.data.email}
                    onChange={e => this.data.email = e.currentTarget.value}
                    type={'email'}
                    ref={e => this.references[3] = e}
                    placeholder={'E-mail'} />
                <span className={'email'} />
            </>) }
        </>
    }

    step4() {
        return <>
            <img src={require(`assets/images/finish_reg.png`)} className={'mb-5'} alt={''} width={100} />

            <h1>Hurra! Profil erstellt!</h1>

            <p>Beginne sofort, indem du dich mit der E-Mail-Adresse unten anmeldest. Wir haben dir auch eine Bestätigungs-E-Mail geschickt.</p>
            <p style={{ marginTop: 24 }}>{ this.data.email }</p>
        </>
    }

    render() {
        const { match, location } = this.props;

        return <div className={'session-page st-wrapper'}>
            <form onSubmit={e => { e.preventDefault(); this.validate() }}>
                <div className={'st-register-wrapper'}>
                    <>
                        <Route path={`${match.path}/1`} render={props => this.step1()} />
                        <Route path={`${match.path}/2`} render={props => this.step2()} />
                        <Route path={`${match.path}/3`} render={props => this.step3()} />
                        <Route path={`${match.path}/4`} render={props => this.step4()} />
                    </>

                    <div className={`st-button step-${this.step}`}>
                        <Button
                            type={'submit'}
                            className={'btn loading-button loading btn-lg btn-primary'}
                        >
                            {this.step === 3 ? 'Registrieren' : 'Weiter'}
                            { this.loading && <div className={'loading-overlay'}><div className="lds-dual-ring" /></div> }
                        </Button>
                    </div>
                </div>
            </form>
        </div>
    }

}
